/**
 * Landing Page Component
 *
 * Redesigned landing page for Equna:
 * - Clean, modern design with clear value proposition
 * - Segment selection (Couples vs Co-Parents)
 * - Emotional connection through illustrations
 * - Social proof and trust signals
 *
 * Uses brand colors: #237CCA (Logo-Blau) and #10b981 (Emerald-500)
 */
import { RotatingText } from '@/components/animations/RotatingText'
import { Logo } from '@/components/branding/Logo'
import { FAQSection } from '@/components/FAQSection'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { RoadmapSection } from '@/components/roadmap/RoadmapSection'
import { StoreBadges } from '@/components/StoreBadges'
import { StoryCarousel } from '@/components/StoryCarousel'
import { Tooltip } from '@/components/Tooltip'
import { UserStorySection } from '@/components/UserStorySection'
import {
    CheckCircle2,
    Home,
    Menu,
    PlayCircle,
    ShieldCheck,
    X
} from 'lucide-react'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { GridRunnerBackground } from '../components/backgrounds/GridRunnerBackground'
import { SEO } from '../components/seo/SEO'
import { HandPhoneMockup } from '../components/visuals/HandPhoneMockup'


// SVG Illustrations
import datenschutzIllustration from '@/assets/illustrations/datenschutz.svg'
import geldtraurigIllustration from '@/assets/illustrations/geldtraurig.svg'
import handshakeIllustration from '@/assets/illustrations/handshake.svg'
import highfiveIllustration from '@/assets/illustrations/highfive.svg'
import streitIllustration from '@/assets/illustrations/streit.svg'
import taschenrechnerIllustration from '@/assets/illustrations/taschenrechner.svg'
import transparenzIllustration from '@/assets/illustrations/tranzparenzhinter.svg'
import traurigIllustration from '@/assets/illustrations/traurig.svg'

export default function LandingPage() {
  const { t, i18n } = useTranslation()
  const [couplesResetKey, setCouplesResetKey] = useState(0)
  const [coparentsResetKey, setCoparentsResetKey] = useState(0)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isAudienceMenuOpen, setIsAudienceMenuOpen] = useState(false)
  const audiencePaths = {
    couples: t('audiencePaths.couples'),
    coparents: t('audiencePaths.coparents'),
    families: t('audiencePaths.families'),
  }
  const primaryStoreUrl = t('storeLinks.primary', { defaultValue: '' })
  const hasStoreLink = primaryStoreUrl.trim().length > 0
  const isEnglish = i18n.language?.toLowerCase().startsWith('en')
  const guideLinks = [
    {
      to: isEnglish
        ? '/blog/split-household-costs-five-models'
        : '/blog/haushaltskosten-fair-teilen-fuenf-modelle',
      title: t('seoGuides.items.household.title'),
      description: t('seoGuides.items.household.description'),
    },
    {
      to: isEnglish
        ? '/blog/equna-vs-splitwise-for-couples-and-families'
        : '/blog/equna-vs-splitwise-fuer-paare-und-familien',
      title: t('seoGuides.items.splitwise.title'),
      description: t('seoGuides.items.splitwise.description'),
    },
    {
      to: isEnglish
        ? '/blog/equna-vs-tricount-for-couples-and-families'
        : '/blog/equna-vs-tricount-fuer-paare-und-familien',
      title: t('seoGuides.items.tricount.title'),
      description: t('seoGuides.items.tricount.description'),
    },
    {
      to: isEnglish
        ? '/blog/3-account-model-and-equna-why-they-belong-together'
        : '/blog/3-konten-modell-und-equna-warum-beides-zusammengehoert',
      title: t('seoGuides.items.accounts.title'),
      description: t('seoGuides.items.accounts.description'),
    },
  ]



  // Couples-specific stories
  const couplesStories = [
    {
      image: geldtraurigIllustration,
      title: t('stories.couples.items.0.title'),
      subtitle: t('stories.couples.items.0.subtitle'),
      description: t('stories.couples.items.0.description'),
      mood: 'problem'
    },
    {
      image: streitIllustration,
      title: t('stories.couples.items.1.title'),
      subtitle: t('stories.couples.items.1.subtitle'),
      description: t('stories.couples.items.1.description'),
      mood: 'problem'
    },
    {
      image: traurigIllustration,
      title: t('stories.couples.items.2.title'),
      subtitle: t('stories.couples.items.2.subtitle'),
      description: t('stories.couples.items.2.description'),
      mood: 'problem'
    },
    {
      image: taschenrechnerIllustration,
      title: t('stories.couples.items.3.title'),
      subtitle: t('stories.couples.items.3.subtitle'),
      description: t('stories.couples.items.3.description'),
      mood: 'solution'
    },
    {
      image: transparenzIllustration,
      title: t('stories.couples.items.4.title'),
      subtitle: t('stories.couples.items.4.subtitle'),
      description: t('stories.couples.items.4.description'),
      mood: 'positive'
    },
    {
      image: handshakeIllustration,
      title: t('stories.couples.items.5.title'),
      subtitle: t('stories.couples.items.5.subtitle'),
      description: t('stories.couples.items.5.description'),
      mood: 'positive'
    },
    {
      image: highfiveIllustration,
      title: t('stories.couples.items.6.title'),
      subtitle: t('stories.couples.items.6.subtitle'),
      description: t('stories.couples.items.6.description'),
      mood: 'positive'
    },
    {
      image: datenschutzIllustration,
      title: t('stories.couples.items.7.title'),
      subtitle: t('stories.couples.items.7.subtitle'),
      description: t('stories.couples.items.7.description'),
      mood: 'positive'
    }
  ]

  // Co-Parents-specific stories
  const coparentsStories = [
    {
      image: geldtraurigIllustration,
      title: t('stories.coparents.items.0.title'),
      subtitle: t('stories.coparents.items.0.subtitle'),
      description: t('stories.coparents.items.0.description'),
      mood: 'problem'
    },
    {
      image: streitIllustration,
      title: t('stories.coparents.items.1.title'),
      subtitle: t('stories.coparents.items.1.subtitle'),
      description: t('stories.coparents.items.1.description'),
      mood: 'problem'
    },
    {
      image: traurigIllustration,
      title: t('stories.coparents.items.2.title'),
      subtitle: t('stories.coparents.items.2.subtitle'),
      description: t('stories.coparents.items.2.description'),
      mood: 'problem'
    },
    {
      image: taschenrechnerIllustration,
      title: t('stories.coparents.items.3.title'),
      subtitle: t('stories.coparents.items.3.subtitle'),
      description: t('stories.coparents.items.3.description'),
      mood: 'solution'
    },
    {
      image: transparenzIllustration,
      title: t('stories.coparents.items.4.title'),
      subtitle: t('stories.coparents.items.4.subtitle'),
      description: t('stories.coparents.items.4.description'),
      mood: 'positive'
    },
    {
      image: handshakeIllustration,
      title: t('stories.coparents.items.5.title'),
      subtitle: t('stories.coparents.items.5.subtitle'),
      description: t('stories.coparents.items.5.description'),
      mood: 'positive'
    },
    {
      image: highfiveIllustration,
      title: t('stories.coparents.items.6.title'),
      subtitle: t('stories.coparents.items.6.subtitle'),
      description: t('stories.coparents.items.6.description'),
      mood: 'positive'
    },
    {
      image: datenschutzIllustration,
      title: t('stories.coparents.items.7.title'),
      subtitle: t('stories.coparents.items.7.subtitle'),
      description: t('stories.coparents.items.7.description'),
      mood: 'positive'
    }
  ]



  // Scroll to section
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const renderStoreCta = (label: string, className: string, icon?: ReactNode) => {
    if (hasStoreLink) {
      if (primaryStoreUrl.startsWith('#')) {
        return (
          <button
            type="button"
            onClick={() => scrollToSection(primaryStoreUrl.slice(1))}
            className={className}
          >
            {icon}
            {label}
          </button>
        )
      }

      return (
        <a
          href={primaryStoreUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={className}
        >
          {icon}
          {label}
        </a>
      )
    }

    const tooltipClassName = className.includes('w-full') ? 'w-full block text-left' : 'inline-block text-left'

    return (
      <Tooltip content={t('nav.comingSoonTooltip')} className={tooltipClassName} position="bottom">
        <button disabled className={`${className} opacity-80 cursor-not-allowed pointer-events-none`}>
          {icon}
          {label}
        </button>
      </Tooltip>
    )
  }

  return (
    <div className="landing-page-root relative min-h-screen bg-slate-50 text-slate-900 selection:bg-[#237CCA] selection:text-white font-nunito overflow-x-hidden">
      <SEO
        title="Equna - Ausgaben teilen für Paare, Eltern & Familien"
        description="Equna ist die App für Paare, Eltern, Co-Parents und Familien: gemeinsame Ausgaben erfassen, fair aufteilen, Belege speichern und monatlich abrechnen."
        canonical="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://equna.eu/#organization",
              "name": "Equna",
              "url": "https://equna.eu",
              "logo": {
                "@type": "ImageObject",
                "url": "https://equna.eu/logo.svg"
              },
              "sameAs": [],
              "description": "Equna ist eine Finanz-App für Paare, Eltern, Co-Parents und Familien, um gemeinsame Ausgaben fair zu teilen. Entwickelt in Deutschland, DSGVO-konform."
            },
            {
              "@type": "WebSite",
              "@id": "https://equna.eu/#website",
              "url": "https://equna.eu",
              "name": "Equna",
              "publisher": { "@id": "https://equna.eu/#organization" },
              "inLanguage": ["de-DE", "en-US"]
            },
            {
              "@type": "SoftwareApplication",
              "@id": "https://equna.eu/#app",
              "name": "Equna",
              "applicationCategory": "FinanceApplication",
              "operatingSystem": "iOS, Android, Web",
              "description": "Equna hilft Paaren, Eltern, Co-Parents und Familien, gemeinsame Ausgaben fair zu teilen. Transparente Kostenaufteilung, Budgets, Belege und monatliche Abrechnung. Made in Germany.",
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "EUR",
                "description": "Kostenlose Grundversion mit allen wichtigen Features"
              },
              "featureList": [
                "Gemeinsame Ausgaben erfassen",
                "Faire Kostenaufteilung (50/50 oder individuell)",
                "Beleg-Fotos hochladen",
                "Monatsabschluss mit Ausgleichsberechnung",
                "Kind-Ausgaben für Co-Parents",
                "Budget-Übersicht",
                "DSGVO-konform, Made in Germany"
              ]
            },
            {
              "@type": "FAQPage",
              "@id": "https://equna.eu/#faq",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "Was ist Equna?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Equna ist eine Finanz-App für Paare, Eltern, Co-Parents und Familien, um gemeinsame Ausgaben fair und transparent zu teilen. Die App ermöglicht es, Haushaltskosten, Kinderkosten und gemeinsame Budgets zu erfassen, automatisch aufzuteilen und am Monatsende den Ausgleich zu berechnen."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Ist Equna kostenlos?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Ja, Equna bietet eine kostenlose Grundversion mit allen wichtigen Features: Ausgaben erfassen, Beleg-Fotos hochladen, 3 Budget-Bereiche, Monatsabschluss und umfangreiche Benachrichtigungen. Eine Premium-Version mit erweiterten Features (Jahresbudgets, erweiterte Analysen, längere Beleg-Speicherung) ist für 4,99€/Monat geplant."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Wie funktioniert die Kostenaufteilung bei Equna?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Bei Equna kannst du eine globale Verteilung festlegen (z.B. 50/50, 60/40 oder nach Einkommen). Jede Ausgabe wird automatisch nach dieser Regel aufgeteilt. Am Monatsende berechnet die App, wer wem wie viel schuldet - basierend auf allen erfassten Ausgaben."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Für wen ist Equna gedacht?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Equna ist für Paare, Eltern, Co-Parents und Familien gedacht, die gemeinsame Ausgaben transparent erfassen und fair aufteilen möchten. Besonders hilfreich ist Equna bei Haushaltskosten, Kinderkosten, gemeinsamen Budgets und monatlichen Abrechnungen."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Welche App hilft Paaren beim Teilen gemeinsamer Ausgaben?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Equna hilft Paaren, gemeinsame Ausgaben wie Miete, Einkäufe, Strom oder Anschaffungen transparent zu erfassen, fair aufzuteilen und am Monatsende abzurechnen."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Wie kann man Haushaltskosten fair aufteilen?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Haushaltskosten können 50/50, anteilig nach Einkommen oder mit einem eigenen Verteilungsschlüssel geteilt werden. In Equna wird diese Regel einmal festgelegt und danach automatisch auf gemeinsame Ausgaben angewendet."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Wie teilen getrennte Eltern Kinderkosten transparent?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Getrennte Eltern und Co-Parents können Kinderkosten in Equna separat erfassen, Belege hinzufügen und die Kosten nach einer vereinbarten Regel aufteilen."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Ist Equna eine Alternative zu Splitwise oder Tricount?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Equna ist eine Alternative, wenn es um wiederkehrende gemeinsame Ausgaben im Alltag geht. Die App ist auf Paare, Eltern, Co-Parents, Familien, Monatsabschlüsse, Kinderkosten und gemeinsame Budgets ausgerichtet."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Sind meine Daten bei Equna sicher?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Ja, Datenschutz hat bei Equna höchste Priorität. Die App ist Made in Germany und DSGVO-konform. Ihre Inhaltsdaten (Ausgaben, Budgets, Belege, Konto) werden ausschließlich auf deutschen IONOS-Servern gespeichert und nicht an Dritte weitergegeben. Sobald Push-Benachrichtigungen aktiviert werden, nutzen wir Firebase (Google) — dabei werden nur technische Trigger ohne persönliche Inhalte übermittelt. Aktuell gibt es keine Werbung, keine Tracking-Cookies und keine Datenweitergabe zu Werbezwecken."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Kann ich Belege für Ausgaben hochladen?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Ja, bei jeder Ausgabe kannst du ein Foto des Belegs hochladen. In der kostenlosen Version werden Belege 60 Tage gespeichert, in der Premium-Version 1 Jahr. Das schafft Transparenz und Nachweisbarkeit bei gemeinsamen Ausgaben."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Was mache ich bei Online-Käufen ohne Kassenzettel?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Kein Problem – du kannst auch Screenshots als Beleg hochladen: z.B. die Bestellbestätigung, eine E-Mail-Rechnung oder die Buchung aus deiner Banking-App. Einfach Screenshot machen, speichern und in Equna hochladen."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Was sind Kind-Ausgaben bei Equna?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Kind-Ausgaben sind ein spezielles Feature für Co-Parents (getrennt lebende Eltern). Du kannst Ausgaben direkt einem Kind zuordnen (z.B. Schulgebühren, Kleidung, Hobbys) und diese separat von den gemeinsamen Haushaltskosten verwalten und aufteilen."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Wie funktioniert der Monatsabschluss?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Am Ende jedes Monats können beide Partner den Monat bestätigen. Die App berechnet dann automatisch den Ausgleich: Wer hat mehr bezahlt? Wer schuldet wem wie viel? So gibt es keine Diskussionen mehr über Geld."
                  }
                }
              ]
            }
          ]
        }}
      />
      {/* BACKGROUND ANIMATION LAYER */}
      <GridRunnerBackground />

      {/* Header / Navigation */}
      <nav className="fixed z-50 transition-all duration-300 bg-white/80 w-full border-slate-200/60 border-b top-0 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <button 
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} 
              className="flex items-center gap-2 group cursor-pointer hover:opacity-80 transition-opacity"
            >
              <Logo size="lg" variant="image" />
              <div className="block">
                <div className="flex items-center">
                  <span className="text-xl font-bold font-nunito" style={{ color: '#237CCA' }}>Eq</span>
                  <span className="text-xl font-bold font-nunito" style={{ color: '#10B981' }}>una</span>
                </div>
                <p className="text-[10px] text-slate-500 font-bold tracking-wider uppercase font-nunito leading-tight">fair teilen</p>
              </div>
            </button>

            {/* Desktop Links */}
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('how-it-works')} className="hover:text-[#237CCA] transition-colors text-sm font-semibold text-slate-600 font-nunito">
                {t('nav.howItWorks')}
              </button>
              <div
                className="relative"
                onMouseEnter={() => setIsAudienceMenuOpen(true)}
                onMouseLeave={() => setIsAudienceMenuOpen(false)}
              >
                <button
                  type="button"
                  className="text-sm font-semibold text-slate-600 hover:text-[#237CCA] transition-colors font-nunito"
                  aria-haspopup="true"
                  aria-expanded={isAudienceMenuOpen}
                >
                  {t('nav.forWhom')} ▾
                </button>
                {isAudienceMenuOpen && (
                  <div className="absolute left-0 top-full pt-3">
                    <div className="bg-white border border-slate-200 rounded-2xl shadow-lg p-2 min-w-[180px]">
                      <Link to={audiencePaths.couples} className="block px-3 py-2 text-sm font-semibold text-slate-700 hover:text-[#237CCA] hover:bg-slate-50 rounded-lg font-nunito">
                        {t('nav.forWhomCouples')}
                      </Link>
                      <Link to={audiencePaths.coparents} className="block px-3 py-2 text-sm font-semibold text-slate-700 hover:text-[#237CCA] hover:bg-slate-50 rounded-lg font-nunito">
                        {t('nav.forWhomCoParents')}
                      </Link>
                      <Link to={audiencePaths.families} className="block px-3 py-2 text-sm font-semibold text-slate-700 hover:text-[#237CCA] hover:bg-slate-50 rounded-lg font-nunito">
                        {t('nav.forWhomFamilies')}
                      </Link>
                    </div>
                  </div>
                )}
              </div>
              <button onClick={() => scrollToSection('faq')} className="text-sm font-semibold text-slate-600 hover:text-[#237CCA] transition-colors font-nunito">
                {t('nav.faq')}
              </button>
              <button onClick={() => scrollToSection('pricing')} className="text-sm font-semibold text-slate-600 hover:text-[#237CCA] transition-colors font-nunito">
                {t('nav.pricing')}
              </button>
              <Link to="/blog" className="text-sm font-semibold text-slate-600 hover:text-[#237CCA] transition-colors font-nunito">
                {t('nav.blog')}
              </Link>
              {/* Roadmap Nav-Link - Auskommentiert bis Feature aktiv beworben werden soll
              <button onClick={() => scrollToSection('roadmap')} className="text-sm font-semibold text-slate-600 hover:text-[#237CCA] transition-colors font-nunito">
                {t('roadmap.title')}
              </button>
              */}
            </div>

            {/* CTA */}
            <div className="flex items-center gap-4">
              <div className="hidden sm:block">
                <LanguageSwitcher />
              </div>
              <a href="/login" className="hidden sm:block text-sm font-semibold text-slate-600 hover:text-slate-900 font-nunito">
                {t('nav.login')}
              </a>
              {renderStoreCta(
                t(hasStoreLink ? 'nav.download' : 'nav.comingSoon', { defaultValue: t('nav.comingSoon') }),
                'hidden md:flex items-center justify-center px-4 py-2 text-sm font-bold text-white bg-[#237CCA] rounded-full font-nunito'
              )}

              {/* Mobile Menu Toggle */}
              <button 
                className="md:hidden p-2 text-slate-600 hover:text-[#237CCA] transition-colors"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white/95 backdrop-blur-md absolute top-16 left-0 w-full shadow-lg animate-in slide-in-from-top-2 duration-200">
            <div className="flex flex-col p-4 space-y-4">
              <button onClick={() => { scrollToSection('how-it-works'); setIsMobileMenuOpen(false) }} className="text-left px-4 py-2 text-slate-600 hover:text-[#237CCA] font-semibold font-nunito hover:bg-slate-50 rounded-lg">
                {t('nav.howItWorks')}
              </button>
              <div className="px-4 pt-2 pb-1 text-xs font-bold tracking-wider uppercase text-slate-400">
                {t('nav.forWhom')}
              </div>
              <Link to={audiencePaths.couples} onClick={() => setIsMobileMenuOpen(false)} className="text-left px-4 py-2 text-slate-600 hover:text-[#237CCA] font-semibold font-nunito hover:bg-slate-50 rounded-lg">
                {t('nav.forWhomCouples')}
              </Link>
              <Link to={audiencePaths.coparents} onClick={() => setIsMobileMenuOpen(false)} className="text-left px-4 py-2 text-slate-600 hover:text-[#237CCA] font-semibold font-nunito hover:bg-slate-50 rounded-lg">
                {t('nav.forWhomCoParents')}
              </Link>
              <Link to={audiencePaths.families} onClick={() => setIsMobileMenuOpen(false)} className="text-left px-4 py-2 text-slate-600 hover:text-[#237CCA] font-semibold font-nunito hover:bg-slate-50 rounded-lg">
                {t('nav.forWhomFamilies')}
              </Link>
              <button onClick={() => { scrollToSection('faq'); setIsMobileMenuOpen(false) }} className="text-left px-4 py-2 text-slate-600 hover:text-[#237CCA] font-semibold font-nunito hover:bg-slate-50 rounded-lg">
                {t('nav.faq')}
              </button>
              <button onClick={() => { scrollToSection('pricing'); setIsMobileMenuOpen(false) }} className="text-left px-4 py-2 text-slate-600 hover:text-[#237CCA] font-semibold font-nunito hover:bg-slate-50 rounded-lg">
                {t('nav.pricing')}
              </button>
              <Link to="/blog" onClick={() => setIsMobileMenuOpen(false)} className="text-left px-4 py-2 text-slate-600 hover:text-[#237CCA] font-semibold font-nunito hover:bg-slate-50 rounded-lg">
                {t('nav.blog')}
              </Link>

              <div className="border-t border-slate-100 my-2 pt-2 flex items-center justify-between px-4">
                 <a href="/login" className="text-slate-600 hover:text-slate-900 font-semibold font-nunito">
                  {t('nav.login')}
                </a>
                <LanguageSwitcher />
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <main className="lg:pt-48 lg:pb-32 overflow-hidden pt-36 pb-20 relative z-10">
        <div className="sm:px-6 lg:px-8 max-w-7xl mx-auto px-4 relative">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-8 gap-y-16 items-center">
            {/* Left Content */}
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-100 text-[#237CCA] mb-8">
                <span className="relative flex h-2 w-2">
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#237CCA]"></span>
                </span>
                <span className="text-xs font-bold tracking-wide uppercase font-nunito">
                  {t('hero.launchBadge')}
                </span>
              </div>

              <h1 className="sm:text-6xl lg:text-7xl leading-[1.1] text-5xl font-bold text-slate-900 tracking-tight font-nunito mb-6">
                <span className="block">{t('hero.titlePrefix')}</span>
                <span className="block">
                  <RotatingText words={[t('hero.rotatingWords.fair'), t('hero.rotatingWords.simple'), t('hero.rotatingWords.transparent')]} interval={2500} />
                </span>
                <span className="block">{t('hero.titleSuffix')}</span>
              </h1>

              <p className="text-lg sm:text-xl text-slate-600 leading-relaxed mb-8 max-w-lg font-nunito font-medium">
                {t('hero.description')}
              </p>

              <div id="download" className="flex flex-col gap-4 mb-12 scroll-mt-24">
                <StoreBadges />
                <button onClick={() => scrollToSection('how-it-works')} className="glow-pulse inline-flex items-center justify-center transition-all text-base font-bold text-slate-700 font-nunito bg-white rounded-full pt-3.5 pr-8 pb-3.5 pl-8 hover:bg-blue-50/50 self-start">
                  <PlayCircle className="mr-2" width={20} height={20} strokeWidth={2} />
                  {t('hero.buttons.howItWorks')}
                </button>
              </div>

              {/* Social Proof */}
              {/* Social Proof */}
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center border border-emerald-100">
                    <ShieldCheck className="w-6 h-6 text-emerald-600" />
                </div>
                <div className="flex flex-col">
                    <span className="font-bold text-slate-900 font-nunito leading-tight">{t('hero.trust.main')}</span>
                    <span className="text-sm text-slate-500 font-nunito font-medium">{t('hero.trust.sub')}</span>
                </div>
              </div>
            </div>

            {/* Right Visual (Phone Mockup) */}
            <HandPhoneMockup />
          </div>
        </div>
      </main>

      {/* Audience Positioning */}
      <section className="py-16 bg-white relative z-10">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 xl:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-5">
              {t('audience.title')}
            </h2>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed font-nunito">
              {t('audience.description')}
            </p>
          </div>
        </div>
      </section>

      {/* SEO Guide Links */}
      <section className="py-16 bg-slate-50 relative z-10">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 xl:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">
                {t('seoGuides.title')}
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                {t('seoGuides.description')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {guideLinks.map((guide) => (
                <Link
                  key={guide.to}
                  to={guide.to}
                  className="group bg-white border border-slate-200 rounded-2xl p-5 text-left shadow-sm hover:shadow-lg hover:border-[#237CCA]/40 transition-all"
                >
                  <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-[#237CCA] transition-colors">
                    {guide.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {guide.description}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>


      {/* 2. Mini-Segmentation */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 xl:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">
              {t('segmentation.title')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Kachel 1: Zusammen lebend */}
            <button
              onClick={() => {
                setCouplesResetKey(prev => prev + 1)
                scrollToSection('couples-story')
              }}
              className="group bg-white rounded-2xl p-8 shadow-md hover:shadow-2xl hover:scale-[1.02] transition-all duration-300 border-2 border-slate-200 text-left relative overflow-hidden"
              style={{ '--hover-border': '#237CCA', '--hover-bg': '#237CCA', '--icon-color': '#237CCA' } as React.CSSProperties}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = '#237CCA'}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
              aria-label={t('segmentation.couples.ariaLabel')}
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 flex-shrink-0 rounded-full flex items-center justify-center transition-colors" style={{ backgroundColor: '#E8F3FB' }}>
                  <Home className="w-8 h-8 group-hover:text-white transition-colors" style={{ color: '#237CCA' }} />
                </div>
                <h3 className="text-xl font-bold text-slate-900">{t('segmentation.couples.title')}</h3>
              </div>
              <p className="text-slate-600">
                {t('segmentation.couples.description')}
              </p>
            </button>

            {/* Kachel 2: Getrennt lebend */}
            <button
              onClick={() => {
                setCoparentsResetKey(prev => prev + 1)
                scrollToSection('coparents-story')
              }}
              className="group bg-white rounded-2xl p-8 shadow-md hover:shadow-2xl hover:scale-[1.02] transition-all duration-300 border-2 border-slate-200 text-left relative overflow-hidden"
              style={{ '--hover-border': '#10b981', '--hover-bg': '#10b981', '--icon-color': '#10b981' } as React.CSSProperties}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = '#10b981'}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
              aria-label={t('segmentation.coparents.ariaLabel')}
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 flex-shrink-0 rounded-full flex items-center justify-center gap-1 transition-colors" style={{ backgroundColor: '#d1fae5' }}>
                  <Home className="w-6 h-6 group-hover:text-white transition-colors" style={{ color: '#10b981' }} />
                  <Home className="w-6 h-6 group-hover:text-white transition-colors" style={{ color: '#10b981' }} />
                </div>
                <h3 className="text-xl font-bold text-slate-900">{t('segmentation.coparents.title')}</h3>
              </div>
              <p className="text-slate-600">
                {t('segmentation.coparents.description')}
              </p>
            </button>
          </div>
        </div>
      </section>

      {/* Couples Story Journey */}
      <section id="couples-story" className="py-20 bg-gradient-to-br from-blue-50 to-slate-100 overflow-hidden">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 xl:px-12">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                {t('stories.couples.title')}
              </h2>
            </div>

            <StoryCarousel 
              stories={couplesStories} 
              themeColor="#237CCA" 
              resetTrigger={couplesResetKey}
            />
          </div>
        </div>
      </section>

      {/* NEW: User Story Section (Screenshots) */}
      <UserStorySection />

      {/* Co-Parents Story Journey */}
      <section id="coparents-story" className="py-20 bg-gradient-to-br from-emerald-50 to-slate-100 overflow-hidden">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 xl:px-12">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                {t('stories.coparents.title')}
              </h2>
            </div>

            <StoryCarousel 
              stories={coparentsStories} 
              themeColor="#10b981" 
              resetTrigger={coparentsResetKey}
            />
          </div>
        </div>
      </section>

      {/* 5. Section: Getrennt Erziehende (RETAINED CONTENT, REMOVED SCREENSHOT) */}

      {/* FAQ Section */}
      <FAQSection />

      {/* 7. Social Proof */}
      <section className="py-20 bg-white relative overflow-hidden">
        {/* Simple Grid Background Pattern */}
        <div className="absolute inset-0 z-0 pointer-events-none opacity-[0.03]" 
             style={{
                 backgroundImage: `linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)`,
                 backgroundSize: '40px 40px'
             }} 
        />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 xl:px-12 relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                {t('testimonials.title')}
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {/* Testimonial 1 */}
              <div className="bg-slate-50 rounded-2xl p-8 space-y-4">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <CheckCircle2 key={i} className="w-5 h-5 text-emerald-500" />
                  ))}
                </div>
                <p className="text-slate-700 italic">
                  {t('testimonials.items.0.quote')}
                </p>
                <p className="text-sm text-slate-600 font-medium">
                  {t('testimonials.items.0.author')}
                </p>
              </div>

              {/* Testimonial 2 */}
              <div className="bg-slate-50 rounded-2xl p-8 space-y-4">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <CheckCircle2 key={i} className="w-5 h-5 text-emerald-500" />
                  ))}
                </div>
                <p className="text-slate-700 italic">
                  {t('testimonials.items.1.quote')}
                </p>
                <p className="text-sm text-slate-600 font-medium">
                  {t('testimonials.items.1.author')}
                </p>
              </div>

              {/* Testimonial 3 */}
              <div className="bg-slate-50 rounded-2xl p-8 space-y-4">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <CheckCircle2 key={i} className="w-5 h-5 text-emerald-500" />
                  ))}
                </div>
                <p className="text-slate-700 italic">
                  {t('testimonials.items.2.quote')}
                </p>
                <p className="text-sm text-slate-600 font-medium">
                  {t('testimonials.items.2.author')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 8. Pricing Section */}
      <section id="pricing" className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 xl:px-12">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                {t('pricing.title')}
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                {t('pricing.description')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {/* Free Tier */}
              <div className="bg-white rounded-3xl p-6 md:p-8 border-2 border-slate-200 flex flex-col h-full">
                <div className="flex-1">
                  <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">{t('pricing.free.title')}</h3>
                  <div className="flex items-baseline justify-center gap-2 mb-4">
                    <span className="text-5xl font-bold text-slate-900">{t('pricing.free.price')}</span>
                    <span className="text-slate-600">{t('pricing.free.period')}</span>
                  </div>
                  <p className="text-slate-600">{t('pricing.free.tagline')}</p>
                  <p className="text-sm text-slate-500 mt-2">{t('pricing.free.subTagline')}</p>
                </div>

                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-900">{t('pricing.free.features.core.title')}</p>
                      <p className="text-sm text-slate-600">{t('pricing.free.features.core.description')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-900">{t('pricing.free.features.budgets.title')}</p>
                      <p className="text-sm text-slate-600">{t('pricing.free.features.budgets.description')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-900">{t('pricing.free.features.receipts.title')}</p>
                      <p className="text-sm text-slate-600">{t('pricing.free.features.receipts.description')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-900">{t('pricing.free.features.distribution.title')}</p>
                      <p className="text-sm text-slate-600">{t('pricing.free.features.distribution.description')}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-900">{t('pricing.free.features.notifications.title')}</p>
                      <p className="text-sm text-slate-600">{t('pricing.free.features.notifications.description')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-900">{t('pricing.free.features.statistics.title')}</p>
                      <p className="text-sm text-slate-600">{t('pricing.free.features.statistics.description')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-900">{t('pricing.free.features.export.title')}</p>
                      <p className="text-sm text-slate-600">{t('pricing.free.features.export.description')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-900">{t('pricing.free.features.ads.title')}</p>
                      <p className="text-sm text-slate-600">{t('pricing.free.features.ads.description')}</p>
                    </div>
                  </div>
                </div>
                </div>

                {renderStoreCta(
                  t(hasStoreLink ? 'pricing.free.ctaAvailable' : 'pricing.free.cta', { defaultValue: t('pricing.free.cta') }),
                  'block w-full px-6 py-3 bg-emerald-500 text-white rounded-full font-bold text-center transition-all duration-300'
                )}
              </div>

              {/* Premium Tier */}
              <div className="bg-gradient-to-br from-sky-50 to-emerald-50 rounded-3xl p-6 md:p-8 border-2 border-sky-500 relative flex flex-col h-full">
                <div className="flex-1">
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-slate-500 text-white text-sm font-bold rounded-full">
                  {t('pricing.premium.badge')}
                </div>

                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">{t('pricing.premium.title')}</h3>
                  <div className="flex items-baseline justify-center gap-2 mb-4">
                    <span className="text-5xl font-bold text-slate-900">{t('pricing.premium.price')}</span>
                    <span className="text-slate-600">{t('pricing.premium.period')}</span>
                  </div>
                  <p className="text-slate-600">{t('pricing.premium.annualPrice')}</p>
                  <p className="text-sm font-semibold text-sky-600 mt-2">{t('pricing.premium.benefits')}</p>
                </div>

                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-sky-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-900">{t('pricing.premium.features.freeIncluded.title')}</p>
                      <p className="text-sm text-slate-600">{t('pricing.premium.features.freeIncluded.description')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-sky-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-900">{t('pricing.premium.features.distributionAdvanced.title')}</p>
                      <p className="text-sm text-slate-600">{t('pricing.premium.features.distributionAdvanced.description')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-sky-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-900">{t('pricing.premium.features.receiptsLong.title')}</p>
                      <p className="text-sm text-slate-600">{t('pricing.premium.features.receiptsLong.description')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-sky-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-900">{t('pricing.premium.features.savings.title')}</p>
                      <p className="text-sm text-slate-600">{t('pricing.premium.features.savings.description')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-sky-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-900">{t('pricing.premium.features.warnings.title')}</p>
                      <p className="text-sm text-slate-600">{t('pricing.premium.features.warnings.description')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-sky-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-900">{t('pricing.premium.features.analytics.title')}</p>
                      <p className="text-sm text-slate-600">{t('pricing.premium.features.analytics.description')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-sky-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-900">{t('pricing.premium.features.backups.title')}</p>
                      <p className="text-sm text-slate-600">{t('pricing.premium.features.backups.description')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-sky-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-900">{t('pricing.premium.features.budgetsAdvanced.title')}</p>
                      <p className="text-sm text-slate-600">{t('pricing.premium.features.budgetsAdvanced.description')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-sky-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-900">{t('pricing.premium.features.annualBudgets.title')}</p>
                      <p className="text-sm text-slate-600">{t('pricing.premium.features.annualBudgets.description')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-sky-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-900">{t('pricing.premium.features.receiptRequirement.title')}</p>
                      <p className="text-sm text-slate-600">{t('pricing.premium.features.receiptRequirement.description')}</p>
                    </div>
                  </div>
                </div>
                </div>

                <button
                  disabled
                  className="block w-full px-6 py-3 bg-slate-500 text-white rounded-full font-bold text-center opacity-75 cursor-not-allowed transition-all duration-300 shadow-lg shadow-slate-500/10"
                >
                  {t('pricing.premium.cta')}
                </button>
              </div>
            </div>

            {/* FAQ / Zusatzinfo */}
            <div className="mt-16 max-w-3xl mx-auto text-center space-y-4">
              <div className="bg-sky-50 border border-sky-200 rounded-2xl p-6">
                <p className="text-sky-900 font-semibold mb-2">{t('pricing.oneSubNote.title')}</p>
                <p className="text-sm text-sky-700">
                  <Trans i18nKey="pricing.oneSubNote.description" components={{ strong: <strong /> }} />
                </p>
              </div>

              <p className="text-slate-600">
                <Trans i18nKey="pricing.vatNote" components={[<strong className="text-slate-900" />]} />
              </p>
              <p className="text-sm text-slate-500">
                {t('pricing.roadmapNote')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Roadmap Section - Ausgeblendet bis Feature aktiv beworben werden soll
      <RoadmapSection />
      */}

      {/* 9. Final CTA */}
      <section className="py-20" style={{ background: 'linear-gradient(to bottom right, #237CCA, #10b981)' }}>
        <div className="container mx-auto px-4 md:px-6 lg:px-8 xl:px-12">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              <span className="whitespace-pre-line">{t('finalCta.title')}</span>
            </h2>
            <StoreBadges className="justify-center" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-900 text-slate-400">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 xl:px-12">
          <div className="max-w-6xl mx-auto grid md:grid-cols-4 gap-8 text-center">

            {/* Product Links */}
            <div>
              <h4 className="font-semibold text-white mb-4">{t('footer.columns.product.title')}</h4>
              <ul className="space-y-2 text-sm">
                 <li><button onClick={() => scrollToSection('how-it-works')} className="hover:text-white transition-colors text-left">{t('footer.columns.product.links.howItWorks')}</button></li>
                 <li><button onClick={() => scrollToSection('couples-story')} className="hover:text-white transition-colors text-left">{t('footer.columns.product.links.forCouples')}</button></li>
                 <li><button onClick={() => scrollToSection('coparents-story')} className="hover:text-white transition-colors text-left">{t('footer.columns.product.links.coParenting')}</button></li>
                 <li><button onClick={() => scrollToSection('pricing')} className="hover:text-white transition-colors text-left">{t('footer.columns.product.links.pricing')}</button></li>
                 <li><Link to="/blog" className="hover:text-white transition-colors">{t('footer.columns.product.links.blog')}</Link></li>
              </ul>
            </div>

            {/* Audience Links (SEO landing pages) */}
            <div>
              <h4 className="font-semibold text-white mb-4">{t('footer.columns.audiences.title')}</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to={audiencePaths.couples} className="hover:text-white transition-colors">{t('footer.columns.audiences.links.couples')}</Link></li>
                <li><Link to={audiencePaths.coparents} className="hover:text-white transition-colors">{t('footer.columns.audiences.links.coparents')}</Link></li>
                <li><Link to={audiencePaths.families} className="hover:text-white transition-colors">{t('footer.columns.audiences.links.families')}</Link></li>
              </ul>
            </div>

            {/* Legal Links */}
            <div>
              <h4 className="font-semibold text-white mb-4">{t('footer.columns.legal.title')}</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/datenschutz" className="hover:text-white transition-colors">{t('footer.columns.legal.links.privacy')}</Link></li>
                <li><Link to="/impressum" className="hover:text-white transition-colors">{t('footer.columns.legal.links.imprint')}</Link></li>
                <li><Link to="/agb" className="hover:text-white transition-colors">{t('footer.columns.legal.links.terms', 'AGB')}</Link></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
               <h4 className="font-semibold text-white mb-4">{t('footer.columns.contact.title')}</h4>
               <ul className="space-y-2 text-sm">
                <li><a href="mailto:contact@equna.eu" className="hover:text-white transition-colors">{t('footer.columns.contact.links.email')}</a></li>
                <li><Link to="/support" className="hover:text-white transition-colors">{t('footer.columns.contact.links.support', 'Support & Hilfe')}</Link></li>
               </ul>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-slate-800 text-center text-sm">
            <p>{t('footer.copyright', { year: new Date().getFullYear() })}</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
